import ThermalPrinterEncoder from "../thermal-printer-encoder"
import { Business } from "../../hooks/api/use-business"
import * as Sentry from "@sentry/react"
import moment from "moment"

export const printBorder = (
  e: ThermalPrinterEncoder,
  width: number,
  options?: {
    noSpacing?: boolean
  }
) => {
  const border = "-".repeat(width)
  let result = e
  if (!options?.noSpacing) {
    result = result.newline()
  }
  result = e.line(border)
  if (!options?.noSpacing) {
    result = result.newline()
  }
  return result
}

export const twoToneText =
  (text: string, value: string) => (e: ThermalPrinterEncoder) => {
    return e.bold(true).text(text).bold(false).text(value)
  }

// print to thermal printer by 512 bytes chunks due to Bluetooth LE limitation
export const printToThermal = async (
  characteristic: BluetoothRemoteGATTCharacteristic,
  data: Uint8Array
) => {
  const dataLength = data.length
  // split into 512 bytes
  const chunkSize = 256
  const chunks = []
  for (let i = 0; i < dataLength; i += chunkSize) {
    chunks.push(data.slice(i, i + chunkSize))
  }
  for (let chunk of chunks) {
    Sentry.setContext("characteristic", {
      uuid: characteristic.uuid,
    })
    // check if the characteristic is writable
    if (!characteristic.properties.write) {
      throw new Error("Characteristic is not writable")
    }
    if (characteristic.writeValueWithResponse) {
      await characteristic.writeValueWithResponse(chunk)
    } else {
      await characteristic.writeValue(chunk)
    }
  }
  // sleep for 1s before sending the next chunk
  // for (let chunk of chunks) {
  //   await characteristic.writeValue(chunk)
  //   await new Promise((resolve) => setTimeout(resolve, 1000))
  // }
}

export const encodeBirHeader = (
  encoder: ThermalPrinterEncoder,
  details: {
    business: Business
    location?: {
      addressLine1?: string
      isVatRegistered?: string
      tin?: string
      companyName?: string
    }
    cashRegister?: {
      min: string
      serialNo: string
    }
    isService: boolean | null
    hasTaxes?: boolean
    locationVatType?: string
  }
) => {
  const invoiceType = details.isService ? "Service Invoice" : "Sales Invoice"

  let result = encoder.newline().bold(true).align("center")
  // let result = encoder.newline().align("center")

  if (details.isService !== null) {
    result = result.line(invoiceType).newline()
  }

  result = result
    .line(details.business.name)
    .line("Operated by: " + details.location?.companyName)
    .line(details.location?.addressLine1 || "")

  if (details.business.country.codeAlpha2 === "PH") {
    result = result
      .line(
        `${details.hasTaxes ? "VAT REG " : ""}TIN: ${details.location?.tin || ""}`
      )
      .line(details.locationVatType || "NON-VAT")
      .line("MIN: " + details.cashRegister?.min)
      .line("S/N: " + details.cashRegister?.serialNo)
  }

  return result.align("left")
}

export const encodeBirFooter = (
  encoder: ThermalPrinterEncoder,
  locationHasTaxes?: boolean,
  cashRegister?: {
    ptu?: string
    ptuDateIssued?: string
  }
) => {
  const formatPtuDate = (value?: string) => {
    if (!value) {
      return "-"
    }
    const date = moment(value)
    return date.isValid() ? date.format("MM/DD/YYYY") : "-"
  }
  // Use the actual PTU number from cash register, fallback to default if missing
  const ptuNumber = cashRegister?.ptu?.trim() || "00000-0000-0000000-0000"

  return encoder
    .align("center")
    .line("POS System Provider")
    .line("DizLog - RigelSoft PH")
    .line("91 2ND FLR. 25D ZETA II BLDG SALCEDO ST. LEGASPI VILLAGE N.A SAN")
    .line("LORENZO, CITY OF MAKATI, FOURTH DISTRICT, NCR, 1229")
    .line(`${locationHasTaxes ? "VAT REG " : ""}TIN 103-286-698-0003`)
    .line("BIR Accreditation #0476002928052025012199")
    .line("Issued: January 27, 2025")
    .line(`PTU No. ${ptuNumber}`)
    .line(`Date Issued: ${formatPtuDate(cashRegister?.ptuDateIssued)}`)
}

export const printSplitTable = (
  encoder: ThermalPrinterEncoder,
  width: number,
  leftWidth: number,
  data: Array<[string, string]>
) => {
  return encoder.table(
    [
      { width: leftWidth - 1, marginRight: 2, align: "left" },
      { width: width - leftWidth - 1, align: "right" },
    ],
    data
  )
}

export const formatPaymentMethod = (
  paymentMethodCode?: string,
  paymentChannel?: string
) => {
  if (!paymentMethodCode) {
    return ""
  }
  if (paymentMethodCode === "CUSTOM") {
    return paymentChannel?.toUpperCase() ?? "CUSTOM"
  }
  if (paymentMethodCode === "CREDIT_NOTE") {
    return "CREDIT NOTE"
  }
  if (paymentMethodCode === "PAY LATER") {
    return "PAY LATER"
  }
  if (paymentMethodCode === "SPLIT_PAYMENT") {
    return "SPLIT PAYMENT"
  }
  return paymentMethodCode
}
